import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Badge,
  Progress,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Icon } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import WorkExecutionAside from "./workExecutionAside";
import { formatCurrency } from "../../utils/Utils";
import { mockApprovedWorkLines } from "../../data/mockWorkExecution";

const { Option } = Select;
const { RangePicker } = DatePicker;

const WorkExecutionClosedWorks: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [approvedWorks, setApprovedWorks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filters, setFilters] = useState({
    contractor: "",
    woNumber: "",
    workLineNumber: "",
    dateRange: null as any,
    completionRange: "",
    flags: "",
  });

  useEffect(() => {
    loadApprovedWorks();
  }, []);

  const loadApprovedWorks = () => {
    setLoading(true);
    try {
      // Load all final approved work lines
      setApprovedWorks(mockApprovedWorkLines);
    } catch (error) {
      console.error("Error loading approved works:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCompletionColor = (percentage: number): string => {
    if (percentage === 100) return "#52c41a";
    if (percentage >= 75) return "#faad14";
    if (percentage >= 50) return "#1890ff";
    return "#ff4d4f";
  };

  const handleViewLineHistory = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/work-execution-work-order-details/${record.woId}?mode=closed&lineId=${record.id}`
    );
  };

  const handleViewSections = (record: any) => {
    navigate(
      `${process.env.PUBLIC_URL}/work-execution-work-order-details/${record.woId}?mode=closed&batchId=${record.id}`
    );
  };

  const resetFilters = () => {
    setFilters({
      contractor: "",
      woNumber: "",
      workLineNumber: "",
      dateRange: null,
      completionRange: "",
      flags: "",
    });
  };

  const applyFilters = () => {
    setDrawerVisible(false);
    // Filter logic would be implemented here
  };

  // KPI calculations
  const totalLines = approvedWorks.length;
  const completedLines = approvedWorks.filter(
    (work) => work.percentCompletion === 100
  ).length;
  const avgCompletion =
    approvedWorks.length > 0
      ? Math.round(
          approvedWorks.reduce((sum, work) => sum + work.percentCompletion, 0) /
            approvedWorks.length
        )
      : 0;
  const withRetention = approvedWorks.filter(
    (work) => work.retentionFlag
  ).length;

  const columns: TableColumnsType<any> = [
    {
      title: "Batch #",
      dataIndex: "workBatchSequenceId",
      key: "workBatchSequenceId",
      width: 180,
      render: (text: string, record: any) => (
        <div>
          <Link
            to={`/work-execution-batch-details/${record.workExeBatchId}`}
            className="fw-bold text-primary"
          >
            {text}
          </Link>
          {/* <div className="text-muted small mt-1">Section: {record.section}</div> */}
        </div>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 200,
      // render: (text, record) => (
      //   <div>
      //     <Link
      //       to={`/work-execution-batch-detail/${record.id}`}
      //       className="fw-bold text-primary"
      //     >
      //       {text}
      //     </Link>
      //     <div className="text-muted small mt-1">Section: {record.section}</div>
      //   </div>
      // ),
    },
    {
      title: "Contractor",
      dataIndex: "contractor",
      key: "contractor",
      width: 180,
    },
    // {
    //   title: "Stage",
    //   dataIndex: "stage",
    //   key: "stage",
    //   width: 120,
    //   render: (stage, record) => (
    //     <div>
    //       <Tag color={getStatusColor(record.status)}>
    //         {getStageIcon(stage)} {stage}
    //       </Tag>
    //     </div>
    //   ),
    // },
    {
      title: "Submitted On/By",
      key: "submitted",
      width: 160,
      render: (_, record) => (
        <div>
          <div className="fw-bold">
            {dayjs(record.submittedOn).format("DD-MMM-YYYY")}
          </div>
          <div className="text-muted small">{record.createdByName}</div>
        </div>
      ),
    },
    {
      title: "Last Action By",
      key: "lastAction",
      width: 160,
      render: (_, record) => (
        <div>
          {record.lastActionBy ? (
            <>
              <div className="fw-bold">{record.lastActionBy}</div>
              <div className="text-muted small">
                {dayjs(record.lastActionDate).format("DD-MMM HH:mm")}
              </div>
            </>
          ) : (
            <span className="text-muted">---</span>
          )}
        </div>
      ),
    },
    // {
    //   title: "SLA Age",
    //   dataIndex: "slaAge",
    //   key: "slaAge",
    //   width: 100,
    //   align: "center",
    //   render: (days) => (
    //     <Badge
    //       count={`${days}d`}
    //       color={days > 2 ? "#ff4d4f" : days > 1 ? "#faad14" : "#52c41a"}
    //     />
    //   ),
    //   sorter: (a, b) => a.slaAge - b.slaAge,
    // },
    // {
    //   title: "Lines",
    //   dataIndex: "totalLines",
    //   key: "totalLines",
    //   width: 80,
    //   align: "center",
    // },
    {
      title: "Total Update Qty",
      dataIndex: "totalUpdateQty",
      key: "totalUpdateQty",
      width: 140,
      align: "right",
      render: (qty) => <strong>{qty?.toLocaleString()}</strong>,
    },
    // {
    //   title: "Flags",
    //   key: "flags",
    //   width: 100,
    //   render: (_, record) => (
    //     <div>
    //       {record.attachments && record.attachments.length > 0 && (
    //         <span title="Evidence Present">📎 </span>
    //       )}
    //       {record.slaAge > 2 && (
    //         <span title="SLA Breach">🔶 </span>
    //       )}
    //     </div>
    //   ),
    // },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 150,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="primary"
    //         size="small"
    //         onClick={() => handleOpenBatch(record)}
    //       >
    //         Open
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <TransactionFlowDefaultTemplate
      moduleKey="work-execution-closed-works"
      title="Approved Works (Closed)"
      columns={columns}
      dataSource={approvedWorks}
      loading={loading}
      sideNavigation={
        <div
          className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
            sm ? "content-active" : ""
          }`}
        >
          <WorkExecutionAside updateSm={setSm} sm={sm} />
        </div>
      }
      extra={{
        tableScrollX: 1800,
        showSearch: true,
        searchPlaceholder: "Search by WO#, Work Line#, or Title...",
        kpiWidgets: [
          {
            title: "Total Approved Lines",
            value: totalLines,
            icon: <CheckCircleOutlined />,
            color: "#52c41a",
          },
          {
            title: "Completed Lines",
            value: completedLines,
            icon: <CalendarOutlined />,
            color: "#1890ff",
          },
          {
            title: "Avg Completion",
            value: `${avgCompletion}%`,
            icon: <UserOutlined />,
            color: "#722ed1",
          },
          {
            title: "With Retention",
            value: withRetention,
            icon: <DollarOutlined />,
            color: "#faad14",
          },
        ],
        advancedFilter: {
          visible: drawerVisible,
          onClose: () => setDrawerVisible(false),
          onOpen: () => setDrawerVisible(true),
          content: (
            <div className="p-4">
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <label className="form-label">Contractor</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Contractor"
                    value={filters.contractor}
                    onChange={(value) =>
                      setFilters({ ...filters, contractor: value })
                    }
                  >
                    <Option value="BuildRight Solutions Ltd">
                      BuildRight Solutions Ltd
                    </Option>
                    <Option value="PowerTech Electrical Services">
                      PowerTech Electrical Services
                    </Option>
                    <Option value="Galaxy Infra">Galaxy Infra</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Order #</label>
                  <Input
                    placeholder="Enter WO Number"
                    value={filters.woNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, woNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Work Line #</label>
                  <Input
                    placeholder="Enter Work Line Number"
                    value={filters.workLineNumber}
                    onChange={(e) =>
                      setFilters({ ...filters, workLineNumber: e.target.value })
                    }
                    allowClear
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">Final Approval Date</label>
                  <RangePicker
                    style={{ width: "100%" }}
                    value={filters.dateRange}
                    onChange={(dates) =>
                      setFilters({ ...filters, dateRange: dates })
                    }
                    placeholder={["From Date", "To Date"]}
                  />
                </Col>
                <Col span={24}>
                  <label className="form-label">% Completion Range</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Completion Range"
                    value={filters.completionRange}
                    onChange={(value) =>
                      setFilters({ ...filters, completionRange: value })
                    }
                  >
                    <Option value="0-25">0% - 25%</Option>
                    <Option value="25-50">25% - 50%</Option>
                    <Option value="50-75">50% - 75%</Option>
                    <Option value="75-99">75% - 99%</Option>
                    <Option value="100">100% (Completed)</Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <label className="form-label">Flags</label>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="Select Flags"
                    value={filters.flags}
                    onChange={(value) =>
                      setFilters({ ...filters, flags: value })
                    }
                  >
                    <Option value="retention">Retention Applied</Option>
                    <Option value="hold">Hold Applied</Option>
                    <Option value="completed">100% Completed</Option>
                  </Select>
                </Col>
              </Row>
              <div className="mt-4 d-flex gap-2">
                <Button
                  type="primary"
                  onClick={applyFilters}
                  style={{ flex: 1 }}
                >
                  Apply Filters
                </Button>
                <Button onClick={resetFilters} style={{ flex: 1 }}>
                  Reset
                </Button>
              </div>
            </div>
          ),
        },
      }}
    />
  );
};

export default WorkExecutionClosedWorks;
